import React, { useEffect, useRef } from "react";
import { useDataTableContext } from "../Context/DataTableContext";
import { useTranslation } from "react-i18next";

export function PaginationLabelComponent({
  pageCount,
  pageIndex,
  hasLoadedBefore: hasLoadedBeforeProp,
}: {
  pageCount?: number;
  pageIndex?: number;
  hasLoadedBefore?: boolean;
}) {
  const { table, isLoading } = useDataTableContext();
  const { t } = useTranslation();
  const pageIdx = pageIndex ?? table?.getState().pagination.pageIndex ?? 0;
  const pageTotalCount = pageCount ?? table?.getPageCount() ?? 0;

  const previousStateRef = useRef<{
    pageIdx: number;
    pageTotalCount: number;
    hasLoadedBefore: boolean;
  }>({
    pageIdx: 0,
    pageTotalCount: 0,
    hasLoadedBefore: false,
  });

  useEffect(() => {
    if (hasLoadedBeforeProp === undefined && !isLoading && pageTotalCount > 0) {
      previousStateRef.current = {
        pageIdx,
        pageTotalCount,
        hasLoadedBefore: true,
      };
    }
  }, [hasLoadedBeforeProp, isLoading, pageIdx, pageTotalCount]);

  const emptyState = (
    <div className="w-full p-6 text-left text-primary-light" role="status">
      {/* Empty but maintains layout */}
    </div>
  );

  const hasLoadedBefore =
    hasLoadedBeforeProp ?? previousStateRef.current.hasLoadedBefore;

  if (isLoading && !hasLoadedBefore) {
    return emptyState;
  }

  if (!isLoading && pageTotalCount === 0) {
    return (
      <div
        className="w-full p-6 text-left text-primary-light"
        role="status"
        aria-label={t("dataTable.noDataAvailable")}
      >
        {t("global.noRows")}
      </div>
    );
  }

  const hasExplicitLoadedProp = hasLoadedBeforeProp !== undefined;
  const hasPreviousLoadedState = previousStateRef.current.hasLoadedBefore;

  const shouldUsePreviousState =
    !hasExplicitLoadedProp && isLoading && hasPreviousLoadedState;

  const displayPageIdx = shouldUsePreviousState
    ? previousStateRef.current.pageIdx
    : pageIdx;

  const displayPageCount = shouldUsePreviousState
    ? previousStateRef.current.pageTotalCount
    : pageTotalCount;

  if (displayPageIdx === undefined || displayPageCount === undefined) {
    return emptyState;
  }

  return (
    <div aria-live="polite" aria-label={t("dataTable.paginationInformation")}>
      <section className="p-6" role="status" aria-live="polite">
        <span
          className="text-primary-light"
          aria-label={t("dataTable.page", {
            page: displayPageIdx + 1,
            totalPages: displayPageCount,
          })}
        >
          {t("dataTable.pageOf", {
            page: displayPageIdx + 1,
            totalPages: displayPageCount,
          })}
        </span>
      </section>
    </div>
  );
}
